import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
interface Purchase {
  id: number;
  name: string;
  city: string;
  product: string;
  price: string;
  image: string;
}
const NAMES = ['Sarah', 'David', 'Elena', 'Michael', 'Jennifer', 'Robert', 'Linda', 'James', 'Patricia', 'William', 'Barbara', 'Richard', 'Susan', 'Joseph', 'Karen', 'Thomas', 'Nancy', 'Charles', 'Betty', 'Daniel'];
const CITIES = ['Tampa, FL', 'Austin, TX', 'San Diego, CA', 'Denver, CO', 'Phoenix, AZ', 'Seattle, WA', 'Atlanta, GA', 'Chicago, IL', 'Portland, OR', 'Nashville, TN', 'Charlotte, NC', 'Columbus, OH'];
const PRODUCTS = [{
  product: 'The Dental Sugar Hack',
  price: '$17',
  image: 'https://cdn.magicpatterns.com/uploads/4N6DPdPYME5x1S7QhhQ6Ch/heylloyd-image-1780001569319.png'
}, {
  product: 'The Complete Bundle',
  price: '$17',
  image: 'https://cdn.magicpatterns.com/uploads/kpoSKbUGebKJ8rW5VoLcqF/ChatGPT_Image_May_28,_2026,_07_18_01_PM.png'
}, {
  product: 'Sugar Hack + Bonuses',
  price: '$17',
  image: 'https://cdn.magicpatterns.com/uploads/rAAXNAKysqkFKhPaGgQQan/ChatGPT_Image_May_28,_2026,_07_19_36_PM_(1).png'
}];
const pickRandom = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];
export function PurchaseToast() {
  const [current, setCurrent] = useState<Purchase | null>(null);
  const recentNamesRef = useRef<string[]>([]);
  const idRef = useRef(0);
  const prefersReducedMotion = useReducedMotion();
  const pickName = useCallback(() => {
    const recent = recentNamesRef.current;
    const available = NAMES.filter(n => !recent.includes(n));
    // If we've exhausted unique names, allow reuse
    const pool = available.length > 0 ? available : NAMES;
    const name = pickRandom(pool);
    // Update rolling history (keep last 10)
    recentNamesRef.current = [...recent, name].slice(-10);
    return name;
  }, []);
  const showToast = useCallback(() => {
    const productInfo = pickRandom(PRODUCTS);
    idRef.current += 1;
    setCurrent({
      id: idRef.current,
      name: pickName(),
      city: pickRandom(CITIES),
      product: productInfo.product,
      price: productInfo.price,
      image: productInfo.image
    });
  }, [pickName]);
  useEffect(() => {
    let dismissTimer: ReturnType<typeof setTimeout>;
    let nextTimer: ReturnType<typeof setTimeout>;
    const cycle = () => {
      showToast();
      // Visible for 4s, then dismiss
      dismissTimer = setTimeout(() => {
        setCurrent(null);
        // Wait a random 6–12s before next toast
        nextTimer = setTimeout(cycle, 6000 + Math.random() * 6000);
      }, 4000);
    };
    // First toast appears after 5s
    const startTimer = setTimeout(cycle, 5000);
    return () => {
      clearTimeout(startTimer);
      clearTimeout(dismissTimer);
      clearTimeout(nextTimer);
    };
  }, [showToast]);
  const variants = prefersReducedMotion ? {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 24
    },
    animate: {
      opacity: 1,
      y: 0
    },
    exit: {
      opacity: 0,
      y: 24
    }
  };
  return <div className="fixed bottom-4 left-4 z-[60] pointer-events-none md:bottom-6 md:left-6" data-id="element-224">
      <AnimatePresence data-id="element-225">
        {current && <motion.div key={current.id} variants={variants} initial="initial" animate="animate" exit="exit" transition={{
        duration: prefersReducedMotion ? 0.2 : 0.4,
        ease: 'easeOut'
      }} role="status" aria-live="polite" className="pointer-events-auto flex items-center gap-3 bg-white rounded-2xl shadow-xl border border-slate-100 p-3 pr-4 max-w-xs" data-id="element-226">
            <div className="w-12 h-12 rounded-xl overflow-hidden bg-slate-100 shrink-0 border border-slate-100" data-id="element-227">
              <img src={current.image} alt={current.product} className="w-full h-full object-cover" data-id="element-228" />
            </div>
            <div className="min-w-0" data-id="element-229">
              <p className="text-sm text-slate-700 leading-snug" data-id="element-230">
                <span className="font-semibold text-slate-900" data-id="element-231">
                  {current.name}
                </span>{' '}
                in {current.city} just purchased{' '}
                <span className="font-semibold text-slate-900" data-id="element-232">
                  {current.product}
                </span>{' '}
                —{' '}
                <span className="font-bold text-orange-500" data-id="element-233">
                  {current.price}
                </span>
              </p>
              <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-234">
                Verified purchase
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}